import { CheckCircleIcon, CloseIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext, useCachedUser } from '@hb/react'
import {
  AssessmentDocument,
  colors,
  ErrorWithMessage,
  getDateString,
  getFullName,
  SignableDocument,
  WithId,
} from '@hb/shared'
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { downloadFromStorage } from '../../../../../backend/storage/utils'
import { ActionButton, SolidActionButton } from '../../../../Buttons'
import { CollapseError } from '../../../../CollapseError'
// import { ContactFormModal, DefaultModal } from '../../../../Modals'
import { ContactFormModal } from '../../../../Modals/Contact/ContactFormModal'
import { DefaultModal } from '../../../../Modals/DefaultModal'
import {
  ElectronicConsentOverlayProvider,
  useElectronicConsent,
  useElectronicConsentOverlay,
} from './context'
// import downloadIcon from '../../../../../icons/'
interface ConsentFormCheckboxProps {
  checked: boolean
  setChecked: (checked: boolean) => void
  registerContainerRef: (el: HTMLDivElement) => void
}

const ConsentFormCheckbox = ({
  children,
  checked,
  setChecked,
  registerContainerRef,
}: PropsWithChildren<ConsentFormCheckboxProps>) => {
  const { toBeSignedByMe } = useElectronicConsent()
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (containerRef.current) {
      registerContainerRef(containerRef.current)
    }
  }, [registerContainerRef])
  return (
    <Box ref={containerRef} w="100%" px={4} py={2}>
      <Checkbox
        isChecked={checked}
        onChange={toBeSignedByMe ? e => setChecked(e.target.checked) : undefined}
        colorScheme="green"
        spacing={3}
        size="lg"
        sx={{
          WebkitTapHighlightColor: 'transparent',
        }}>
        <Text fontSize="md" position="relative" bottom="2px">
          {children}
        </Text>
      </Checkbox>
    </Box>
  )
}

const electonicConsentContactFormId = 'electronic-consent-contact-form'
const ElectronicConsentFooter = ({
  onConfirm,
  allChecked,
  isLoading,
  error,
  withConsentForm,
}: {
  onConfirm: () => void
  allChecked: boolean
  isLoading: boolean
  error: string | null
  withConsentForm?: boolean
}) => {
  const { onClose } = useElectronicConsentOverlay()
  const { mostRecentSubmission } = useElectronicConsent()
  const { showMessage } = useContext(PopUpMessageContext)
  const submitText = useMemo(() => {
    if (allChecked)
      return 'By clicking “Submit”, I acknowledge that I have read and agree to the above statements.'
    return 'Please confirm all statements to submit your electronic consent agreement.'
  }, [allChecked])

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadSubmittedAgreement = useCallback(async () => {
    if (!mostRecentSubmission || isDownloading) return
    const url = mostRecentSubmission.signedStoragePath
    try {
      setIsDownloading(true)
      await downloadFromStorage(url)
      showMessage({
        text: 'Downloaded electronic consent agreement',
        type: 'success',
      })
    } catch (e) {
      console.error(e)
      showMessage({
        text: 'Error downloading electronic consent agreement.',
        type: 'error',
        subText: (e as ErrorWithMessage).message,
      })
    } finally {
      setIsDownloading(false)
    }
  }, [mostRecentSubmission, showMessage, isDownloading])

  return (
    <Flex
      bg="gray.100"
      borderTop="1px solid"
      borderColor="gray.200"
      w="100%"
      flexFlow="column"
      borderBottomRadius={6}>
      <CollapseError error={error} />
      <Collapse style={{ width: '100%' }} in={!mostRecentSubmission}>
        <Flex py={3} px={4} w="100%" flexFlow={['column', 'column', 'row']} align="center" gap={2}>
          <Flex flex={1} minW="0">
            <Text color="gray.700" fontWeight={500}>
              {submitText}
            </Text>
          </Flex>
          <SolidActionButton
            opacity={allChecked ? 1 : 0.6}
            isLoading={isLoading}
            ml="auto"
            onClick={onConfirm}>
            Submit
          </SolidActionButton>
        </Flex>
      </Collapse>
      <Collapse style={{ width: '100%' }} in={!!mostRecentSubmission}>
        <Flex w="100%" flexFlow="column">
          <Flex
            py={3}
            px={4}
            w="100%"
            // flexFlow={['column', 'column', 'row']}
            align="center"
            gap={2}>
            <Text flex={1} color="gray.700" fontWeight={500}>
              You signed this agreement on {getDateString(mostRecentSubmission?.signedOn ?? 0)}. If
              you have any questions or concerns, please contact us or send an email to
              info@hamiltonbilling.com.
            </Text>
            <IconButton
              isLoading={isDownloading}
              icon={<DownloadIcon />}
              bg="blackAlpha.200"
              borderRadius="full"
              aria-label="Download"
              onClick={handleDownloadSubmittedAgreement}
            />
          </Flex>
          {withConsentForm ? (
            <Box w="100%" px={2} pb={2}>
              <SolidActionButton w="100%" onClick={() => onClose()}>
                Proceed to Consent Form
              </SolidActionButton>
            </Box>
          ) : null}
        </Flex>
      </Collapse>
      <ContactFormModal id={electonicConsentContactFormId} />
    </Flex>
  )
}

const ElectronicConsentHeader = () => {
  const { onClose } = useElectronicConsentOverlay()
  const { mostRecentSubmission, config } = useElectronicConsent()
  return (
    <Box
      borderBottom="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      borderTopRadius={6}
      w="100%"
      px={4}
      py={2}>
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontFamily="Hero-New">
          {config.title}
        </Text>
        {!mostRecentSubmission ? (
          <IconButton
            borderRadius="full"
            size="xs"
            w={7}
            h={7}
            color="gray.600"
            bg="blackAlpha.200"
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={() => onClose()}
          />
        ) : null}
      </Flex>
      <Text fontFamily="hero-new">{config.preface}</Text>
    </Box>
  )
}

type ContainerRefItem = HTMLDivElement | null
type ContainerRefTuple = [ContainerRefItem, ContainerRefItem, ContainerRefItem, ContainerRefItem]

const ElectronicConsentFormContent = ({
  onClose,
  withConsentForm,
}: {
  onClose: (forceClose?: boolean) => void
  withConsentForm?: boolean
}) => {
  const { mostRecentSubmission, signerId, config } = useElectronicConsent()
  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState<[boolean, boolean, boolean, boolean]>(
    mostRecentSubmission ? [true, true, true, true] : [false, false, false, false],
  )

  const allChecked = useMemo(() => checked.every(c => c), [checked])

  const handleConfirm = useCallback(async () => {
    if (isConfirming || !signerId) return
    if (!allChecked) {
      setError('Please confirm all statements.')
      return
    }
    setIsConfirming(true)
    setError(null)

    try {
      await config.confirm()
      // Force-close so we only dismiss this agreement overlay/modal. Without the force flag the
      // overlay's onClose sees consentRequired still true (the signed submission hasn't propagated
      // via snapshot yet) and would close the parent document modal instead of just the overlay.
      onClose(true)
    } catch (e) {
      console.error(e)
      setError('Error submitting agreement.')
    } finally {
      setIsConfirming(false)
    }
  }, [signerId, config, isConfirming, allChecked, onClose])

  const checkmarkRefs = useRef<ContainerRefTuple>([null, null, null, null])
  const checkmarksContainerRef = useRef<HTMLDivElement>(null)
  const handleRegisterContainerRef = useCallback(
    (index: number) => (el: HTMLDivElement) => {
      checkmarkRefs.current[index] = el
    },
    [checkmarkRefs],
  )
  const onCheck = useCallback(
    (index: number) => (updated: boolean) => {
      if (mostRecentSubmission) return
      const newChecked = [...checked] as [boolean, boolean, boolean, boolean]
      newChecked[index] = updated
      setChecked(newChecked)
      // scroll to first unchecked checkbox
      if (updated) {
        const firstUncheckedIndex = newChecked.findIndex(c => !c)
        if (firstUncheckedIndex === -1) return
        const checkedHeight = checkmarkRefs.current
          ?.slice(0, firstUncheckedIndex)
          .reduce((acc, curr) => acc + (curr?.clientHeight || 0), 0)

        if (!checkedHeight) return
        checkmarksContainerRef.current?.scrollTo({
          top: checkedHeight,
          behavior: 'smooth',
        })
      }
    },
    [mostRecentSubmission, checked],
  )

  useEffect(() => {
    if (mostRecentSubmission) {
      setChecked([true, true, true, true])
    }
  }, [mostRecentSubmission])
  return (
    <Box w="100%">
      <ElectronicConsentHeader />
      <VStack
        ref={checkmarksContainerRef}
        spacing={0}
        flex={1}
        overflowY="auto"
        flexFlow="column"
        divider={<Divider />}
        w="100%">
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(0)}
          checked={checked[0]}
          setChecked={onCheck(0)}>
          {config.checkboxes[0]}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(1)}
          checked={checked[1]}
          setChecked={onCheck(1)}>
          {config.checkboxes[1]}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(2)}
          checked={checked[2]}
          setChecked={onCheck(2)}>
          {config.checkboxes[2]}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(3)}
          checked={checked[3]}
          setChecked={onCheck(3)}>
          {config.checkboxes[3]}
        </ConsentFormCheckbox>
      </VStack>
      <ElectronicConsentFooter
        onConfirm={handleConfirm}
        withConsentForm={withConsentForm}
        isLoading={isConfirming}
        error={error}
        allChecked={allChecked}
      />
    </Box>
  )
}

const ElectronicConsentForm = ({
  containerHeight,
  containerWidth,
}: {
  containerHeight: number
  containerWidth: number
}) => {
  const { onClose, isOpen } = useElectronicConsentOverlay()
  const { consentLoading } = useElectronicConsent()

  const { width, height } = useMemo(
    () => ({
      width: Math.min(700, containerWidth * 0.95),
      height: Math.min(800, containerHeight * 0.95),
    }),
    [containerHeight, containerWidth],
  )

  return (
    <Flex
      w={`${width}px`}
      bg="white"
      onClick={e => e.stopPropagation()}
      borderRadius={6}
      maxHeight={`${height}px`}
      flexFlow="column"
      opacity={!isOpen || consentLoading ? 0 : 1}
      transition="opacity 0.3s ease-in-out">
      <ElectronicConsentFormContent onClose={onClose} />
    </Flex>
  )
}

export const ElectronicConsentOverlay = ({
  containerHeight,
  containerWidth,
}: {
  containerHeight: number
  containerWidth: number
}) => {
  const { isOpen, consentForm, onClose } = useElectronicConsentOverlay()
  const { mostRecentSubmission, consentLoading } = useElectronicConsent()
  if (!consentForm) return null
  return (
    <Center
      position="absolute"
      opacity={isOpen || consentLoading ? 1 : 0}
      pointerEvents={isOpen || consentLoading ? 'auto' : 'none'}
      onClick={() => {
        if (mostRecentSubmission) {
          onClose()
        }
      }}
      transition="opacity 0.3s ease-in-out"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={9}
      bg="rgba(0,0,0,0.5)">
      <ElectronicConsentForm containerHeight={containerHeight} containerWidth={containerWidth} />
    </Center>
  )
}

export const ElectronicConsentModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => (
  <DefaultModal
    isOpen={isOpen}
    size="2xl"
    overlayHeader
    onClose={onClose}
    render={() => <ElectronicConsentFormContent onClose={onClose} />}
  />
)

export const ElectronicConsentStatusView = () => {
  const { mostRecentSubmission, signerId, toBeSignedByMe, config } = useElectronicConsent()

  const fetchedUserId = useMemo(() => {
    if (toBeSignedByMe) return null
    return signerId
  }, [signerId, toBeSignedByMe])
  const { data: user } = useCachedUser(fetchedUserId)
  const text = useMemo(() => {
    if (toBeSignedByMe) {
      return mostRecentSubmission
        ? `You submitted the ${config.previewTitle} on ${getDateString(mostRecentSubmission.signedOn)}.`
        : `You have not yet submitted the ${config.previewTitle}.`
    }
    return mostRecentSubmission
      ? `${config.previewTitle} submitted`
      : `${config.previewTitle} not yet submitted.`
  }, [mostRecentSubmission, toBeSignedByMe, config])
  const body = (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      borderRadius={4}
      py={1}
      px={3}
      maxW="100%">
      <Text fontSize="sm" fontWeight={500} color="gray.700">
        {text}
      </Text>
    </Flex>
  )
  if (!mostRecentSubmission) {
    return body
  }
  return (
    <Tooltip
      hasArrow
      bg={colors.green.hex}
      color="white"
      textShadow="1px 1px 3px rgba(0,0,0,0.5)"
      label={`Signed by ${user ? getFullName(user) : config.signerFallbackLabel} on ${getDateString(mostRecentSubmission.signedOn, 'short')}`}
      placement="bottom">
      {body}
    </Tooltip>
  )
}

export const ElectronicConsentPreview = ({ openModal }: { openModal: () => void }) => {
  const { mostRecentSubmission, config } = useElectronicConsent()
  const { isMobile } = useContext(ScreenContext)
  const ButtonComponent = mostRecentSubmission ? ActionButton : SolidActionButton
  return (
    <Flex w="100%" gap={2} flexFlow={['column', 'column', 'row']} justifyContent="space-between">
      <Box py={2} px={4} flex={1} minW="0">
        <Text fontSize="lg" fontWeight={500}>
          {config.previewTitle}
        </Text>
        <Flex align="center" gap={1.5}>
          <CheckCircleIcon
            w={4}
            h={4}
            color={mostRecentSubmission ? 'green.500' : 'blackAlpha.500'}
          />
          <Text fontWeight={500} color={mostRecentSubmission ? 'green.500' : 'gray.500'}>
            {mostRecentSubmission
              ? `COMPLETED ${getDateString(mostRecentSubmission.signedOn, 'short')}`
              : 'NOT COMPLETED'}
          </Text>
        </Flex>
      </Box>
      <Flex
        justifyContent="center"
        alignItems="flex-end"
        pb={1}
        pt={3}
        pr={3}
        pl={isMobile ? 3 : 0}
        flexFlow="column">
        <ButtonComponent size="sm" mb={[2, 2, 0]} w={['100%', '100%', 'auto']} onClick={openModal}>
          {mostRecentSubmission ? 'View Completed Form' : 'View Form'}
        </ButtonComponent>
      </Flex>
    </Flex>
  )
}

export const ElectronicConsentViewButton = ({
  variant = 'outline',
}: {
  variant?: 'outline' | 'link'
}) => {
  const { onOpen, consentForm } = useElectronicConsentOverlay()
  const { toBeSignedByMe, config } = useElectronicConsent()
  if (!consentForm) return null
  if (!toBeSignedByMe) {
    return <ElectronicConsentStatusView />
  }
  return (
    <Button bg="whiteAlpha.600" variant={variant} size="sm" onClick={onOpen}>
      {config.previewTitle}
    </Button>
  )
}

export const ElectronicConsentWrapper = ({
  document,
  containerHeight,
  containerWidth,
  onCloseDocument,
  children,
}: PropsWithChildren<{
  document: WithId<SignableDocument> | WithId<AssessmentDocument>
  containerHeight: number
  containerWidth: number
  onCloseDocument: () => void
}>) => (
  <ElectronicConsentOverlayProvider document={document} onCloseDocument={onCloseDocument}>
    <Box width={`${containerWidth}px`} height={`${containerHeight}px`}>
      {children}
      <ElectronicConsentOverlay containerHeight={containerHeight} containerWidth={containerWidth} />
    </Box>
  </ElectronicConsentOverlayProvider>
)
